'use client';
// Auth gate. The session lives in an httpOnly cookie the browser JS can't read, so we probe it with
// `me()` on mount: it succeeds → open the shell; a 401 → no/invalid session, fall through to
// setup-or-login. Any later 401 answering a request issued IN this session fires AUTH_CLEARED_EVENT,
// which flips us straight to the login form and drops cached data — so a stale/expired/deleted-user
// session can't strand the user in a broken shell. A 401 still owed to a session a login has since
// replaced is not one of those and is ignored by the client (lib/token.ts), because acting on it would
// delete the session that login just created.
//
// While the session query is in flight the children (the app shell) ALREADY render: their queries race
// it instead of waiting for it, so the dashboard fills progressively rather than in a two-stage waterfall.
// That is safe because a child only ever renders data its own authenticated fetch returned — an
// unauthenticated visitor sees chrome and skeletons for a beat, every child query 401s, and the first
// 401 flips the gate to the login form (the same end state the probe would reach).
//
// The gate reads the session through the SAME useMe() query the rest of the app uses, rather than
// fetching /auth/me itself and seeding the result. Seeding cannot win the race now that children mount
// immediately: their useMe() fires on the same tick as the gate's own probe, so the app would ask twice.
// Sharing one query key makes the duplicate structurally impossible instead of merely unlikely.
import { Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { usePathname, useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { AUTH_CLEARED_EVENT, AUTH_TRANSITION_EVENT, subscribeAuthTransitions, type AuthTransition } from '../../lib/token';
import { ElowenApiError, elowenClient } from '../../lib/elowenClient';
import { QUERY_KEYS, useMe } from '../../lib/queries';
import { EventBridge } from '../../app/providers';
import { LoginForm } from './LoginForm';
import { LicenceLocked } from './LicenceLocked';
import { SetupPending } from './SetupPending';
import { LoadingState } from '../ui/states';

type Gate = 'checking' | 'transitioning' | 'refreshing' | 'open' | 'login' | 'setup' | 'licence';

export function LoginGate({ children, initiallyAuthenticated = false, sessionPresent = true }: { children: ReactNode; initiallyAuthenticated?: boolean; sessionPresent?: boolean }) {
  // A server-prefetched identity already proved this exact request's httpOnly session. Conversely, the
  // server's absence of a cookie proves it is logged out. Only a present-but-unvalidated cookie checks.
  const [gate, setGate] = useState<Gate>(initiallyAuthenticated ? 'open' : sessionPresent ? 'checking' : 'login');
  const qc = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();
  const [landingPath, setLandingPath] = useState<string | null>(null);
  const transitionId = useRef<string | null>(null);
  const transitionCleanup = useRef<Promise<void>>(Promise.resolve());
  const me = useMe(gate !== 'transitioning' && gate !== 'refreshing' && gate !== 'licence');
  const meSettled = !me.isPending;
  const meFailed = me.error;

  useEffect(() => {
    if ((gate !== 'checking' && gate !== 'open') || !meSettled) return;
    // A 503 on the session probe is the locked daemon, not a logged-out visitor: the 503 effect
    // below probes setup instead, so this lane leaves the gate alone rather than flashing login.
    if (meFailed instanceof ElowenApiError && meFailed.status === 503) return;
    // The session query answers the gate: data means the httpOnly cookie is a valid session → open the
    // shell. A 401 means no/invalid session → on a box where the installer never finished there is no
    // account to sign in as, so we say that instead of showing a login nobody can pass. A transient/
    // network error is treated as "not authed" so we show login rather than a blank gate.
    setGate(meFailed ? 'login' : 'open');
  }, [gate, meSettled, meFailed]);

  // One in-flight setup probe shared by the login lane and the 503 lane below: an anonymous locked
  // visit fires both lanes at once (the login lane on mount, the 503 lane when the session probe
  // answers), and without sharing they race the same gate with two overlapping requests. Sequential
  // probes each get their own request — sharing only collapses true overlap.
  type SetupStatus = Awaited<ReturnType<typeof elowenClient.setupStatus>>;
  const setupProbe = useRef<Promise<SetupStatus> | null>(null);
  const probeSetup = useCallback((): Promise<SetupStatus> => {
    const live = setupProbe.current;
    if (live) return live;
    const next = elowenClient.setupStatus();
    setupProbe.current = next;
    const release = (): void => { if (setupProbe.current === next) setupProbe.current = null; };
    void next.then(release, release);
    return next;
  }, []);

  // Whether "log in" is even meaningful is a separate question from whether this session is valid, so it
  // gets its own probe. Hanging it off the session query does not work: a 401 clears the query cache
  // synchronously, which leaves useMe() pending forever and the probe unreachable — the bug that made the
  // old first-run route dead code. Keyed on the gate, it runs however we arrived at the login screen.
  //
  // The 503 lane below shares the in-flight probe, so this lane only stays out once the session failure
  // is known to be a 503 — anything earlier would stall the logged-out lane, whose 401 settles nothing.
  useEffect(() => {
    if (gate !== 'login') return;
    if (meFailed instanceof ElowenApiError && meFailed.status === 503) return;
    let alive = true;
    probeSetup()
      .then((s) => {
        if (!alive) return;
        // A locked daemon cannot serve the shell OR a login nobody can pass: it gets its own page.
        if (s.licenceLocked) setGate('licence');
        else if (s.needsSetup) setGate('setup');
      })
      .catch(() => { /* unreachable daemon: the login form is still the honest fallback */ });
    return () => { alive = false; };
  }, [gate, meFailed, probeSetup]);

  // A locked daemon answers EVERYTHING else with 503, including the session probe: a prefetched
  // session (`initiallyAuthenticated`) must not hide that behind an open shell. This lane shares the
  // login lane's in-flight probe, so the two never overlap — locked, setup, or login directly, without
  // flashing the login form on the way there.
  useEffect(() => {
    if ((gate !== 'checking' && gate !== 'open' && gate !== 'login') || !meSettled || !(meFailed instanceof ElowenApiError) || meFailed.status !== 503) return;
    let alive = true;
    probeSetup()
      .then((s) => {
        if (!alive) return;
        if (s.licenceLocked) setGate('licence');
        else if (s.needsSetup) setGate('setup');
        else setGate('login');
      })
      .catch(() => { if (alive) setGate('login'); });
    return () => { alive = false; };
  }, [gate, meSettled, meFailed, probeSetup]);

  // An identity transition is coordinated across every tab. `start` synchronously replaces the shell so
  // React cleans up all account-scoped streams before the BFF swaps cookies, then clears and cancels the
  // old cache exactly once. A same-account credential refresh instead keeps the live form/data and
  // route, pausing streams until a fresh /auth/me proves the cookie after commit or rollback.
  useEffect(() => subscribeAuthTransitions((transition) => {
    if (transition.phase === 'ready') return;
    if (transition.phase === 'start') {
      transitionId.current = transition.id;
      setLandingPath(null);
      flushSync(() => setGate(current => transition.refresh && (current === 'open' || current === 'refreshing') ? 'refreshing' : 'transitioning'));
      transitionCleanup.current = qc.cancelQueries().then(() => { if (!transition.refresh) qc.clear(); });
      return;
    }
    const id = transition.id;
    if (transitionId.current !== id) return;
    void transitionCleanup.current.then(async () => {
      if (transitionId.current !== id) return;
      try {
        await qc.fetchQuery({ queryKey: QUERY_KEYS.me, queryFn: elowenClient.me, staleTime: transition.refresh ? 0 : 5 * 60 * 1000 });
        if (transitionId.current !== id) return;
        if (transition.refresh) {
          // This is credential rotation, not an identity switch: preserve the live form and route,
          // then refresh cancelled reads without dropping their last same-account data.
          window.dispatchEvent(new CustomEvent<AuthTransition>(AUTH_TRANSITION_EVENT, {
            detail: { id, phase: 'ready', refresh: true },
          }));
          void qc.invalidateQueries({ predicate: query => query.queryKey[0] !== QUERY_KEYS.me[0] });
          setGate('open');
          return;
        }
        const landing = transition.landing ?? '/dash';
        setLandingPath(new URL(landing, window.location.origin).pathname);
        router.replace(landing);
      } catch {
        if (transitionId.current === id) setGate('login');
      } finally {
        if (transitionId.current === id) transitionId.current = null;
      }
    });
  }), [qc, router]);

  // Navigation is asynchronous: opening immediately after replace remounts the old page under the
  // new account, firing its queries and reconnect invalidations before the landing page arrives.
  useEffect(() => {
    if (gate !== 'transitioning' || landingPath === null || pathname !== landingPath) return;
    setLandingPath(null);
    setGate('open');
  }, [gate, landingPath, pathname]);

  // Token dropped (stale-token validation 401, mid-session 401, or explicit logout): go to login with
  // no reload, and clear the cache so a re-login can never flash the previous user's data.
  useEffect(() => {
    const onCleared = () => { transitionId.current = null; qc.clear(); setGate('login'); };
    window.addEventListener(AUTH_CLEARED_EVENT, onCleared);
    return () => window.removeEventListener(AUTH_CLEARED_EVENT, onCleared);
  }, [qc]);

  // The login form REPLACES the shell (an unauthenticated visitor must not reach the app), but the
  // 'checking' state renders children so the shell and its query fan-out start immediately.
  if (gate === 'transitioning') return <main className="flex min-h-screen items-center justify-center bg-background"><LoadingState /></main>;
  if (gate === 'licence') return <LicenceLocked />;
  if (gate === 'setup') return <SetupPending />;
  if (gate === 'login') return <Suspense fallback={null}><LoginForm onAuthed={() => setGate('open')} /></Suspense>;

  return (
    <>
      {/* The SSE bridge stays gated on a confirmed session: mounted tokenless it would 401 once and
          EventSource has no hook to reconnect after login. */}
      {gate === 'open' ? <EventBridge /> : null}
      {/* Next still server-renders these client pages during the build, and several of them read
          useSearchParams(), which bails out of SSR unless a Suspense boundary catches it. While the gate
          returned null until the probe settled, the prerender never reached that call — now it does, and
          without this boundary `next build` fails on the first such page. One boundary here covers every
          route, so a new page cannot reintroduce the failure by forgetting its own. The fallback is null
          because it is only ever seen by the build: in the browser nothing here suspends. */}
      <Suspense fallback={null}>{children}</Suspense>
    </>
  );
}
